import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import "./photo-upload.css";

import {
  defaultOptions,
  getLocationOrigin,
  checkStatus,
  parseJSON,
} from "../../../../../../../services";

const MAX_DIMENSION = 1920; // 1080p = 1920x1080 (longest side)
const WEBP_QUALITY = 1.0;   // Full quality (lossless-ish)

/**
 * Compresses a File:
 *  - If either dimension exceeds MAX_DIMENSION, scales down proportionally.
 *  - Converts to WebP at full quality.
 * Returns a Promise<File>.
 */
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = function () {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;

      // Resize only if larger than 1080p
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width >= height) {
          height = Math.round((height * MAX_DIMENSION) / width);
          width = MAX_DIMENSION;
        } else {
          width = Math.round((width * MAX_DIMENSION) / height);
          height = MAX_DIMENSION;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        function (blob) {
          if (!blob) {
            // Fallback: use original file if WebP conversion fails
            resolve(file);
            return;
          }

          // Rename to .webp
          const baseName = file.name.replace(/\.[^/.]+$/, "");
          const compressedFile = new File([blob], baseName + ".webp", {
            type: "image/webp",
            lastModified: Date.now(),
          });

          if (compressedFile.size >= file.size) {
            console.log(
              "[compress] " + file.name,
              "\n  Input : " + (file.size / 1024).toFixed(1) + " KB (" + img.naturalWidth + "x" + img.naturalHeight + ")",
              "\n  Output: " + (compressedFile.size / 1024).toFixed(1) + " KB (" + width + "x" + height + ")",
              "\n  Reverted to original (compressed was larger)"
            );
            resolve(file);
            return;
          }

          console.log(
            "[compress] " + file.name,
            "\n  Input : " + (file.size / 1024).toFixed(1) + " KB (" + img.naturalWidth + "x" + img.naturalHeight + ")",
            "\n  Output: " + (compressedFile.size / 1024).toFixed(1) + " KB (" + width + "x" + height + ")",
            "\n  Saved : " + ((1 - compressedFile.size / file.size) * 100).toFixed(1) + "%"
          );

          resolve(compressedFile);
        },
        "image/webp",
        WEBP_QUALITY
      );
    };

    img.onerror = function () {
      URL.revokeObjectURL(objectUrl);
      // Fallback: use original file on error
      resolve(file);
    };

    img.src = objectUrl;
  });
}

class AddAlbumPhotosLater extends Component {
  constructor(props) {
    super(props);

    this.state = {
      uploadedFiles: [],
      isDragging: false,
      uploading: false,
    };

    this.fileInput = null;
  }

  componentWillUnmount() {
    this.state.uploadedFiles.forEach((file) => {
      URL.revokeObjectURL(file.preview);
    });
  }

  handleFileSelect = (files) => {
    if (!files) return;

    const rawFiles = Array.from(files);

    // Build entries with "compressing" status immediately for UI feedback
    const pendingEntries = rawFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file: file,           // original, will be replaced after compression
      preview: URL.createObjectURL(file),
      status: "compressing",
    }));

    this.setState((prev) => ({
      uploadedFiles: prev.uploadedFiles.concat(pendingEntries),
    }));

    // Compress each file, then swap in the compressed version
    pendingEntries.forEach((entry) => {
      compressImage(entry.file).then((compressedFile) => {
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
        if (compressedFile.size > MAX_FILE_SIZE) {
          console.log(
            "[compress] " + entry.file.name,
            "\n  Final size: " + (compressedFile.size / 1024 / 1024).toFixed(2) + " MB — exceeds 5MB limit, upload blocked"
          );
          this.setState((prev) => ({
            uploadedFiles: prev.uploadedFiles.map((f) =>
              f.id === entry.id
                ? { ...f, file: compressedFile, status: "too_large" }
                : f
            ),
          }));
          return;
        }

        this.setState((prev) => ({
          uploadedFiles: prev.uploadedFiles.map((f) =>
            f.id === entry.id
              ? { ...f, file: compressedFile, status: "ready" }
              : f
          ),
        }));
      });
    });
  };

  removeFile = (fileId) => {
    this.setState((prev) => {
      const file = prev.uploadedFiles.find((f) => f.id === fileId);

      if (file) {
        URL.revokeObjectURL(file.preview);
      }

      return {
        uploadedFiles: prev.uploadedFiles.filter((f) => f.id !== fileId),
      };
    });
  };

  openFileBrowser = () => {
    if (this.fileInput) {
      this.fileInput.click();
    }
  };

  handleSubmit = () => {
    const { uploadedFiles } = this.state;
    const albumId = this.props.match.params.albumId;

    if (!albumId || uploadedFiles.length === 0) return;

    // Don't upload while any file is still compressing
    const stillCompressing = uploadedFiles.some((f) => f.status === "compressing");
    if (stillCompressing) return;

    // Only upload files that passed the size check
    const filesToUpload = uploadedFiles.filter((f) => f.status !== "too_large");
    if (filesToUpload.length === 0) return;

    this.setState({ uploading: true });

    this.setState((prev) => ({
      uploadedFiles: prev.uploadedFiles.map((f) =>
        f.status === "too_large" ? f : { ...f, status: "uploading" }
      ),
    }));

    const uploadNext = (index) => {
      if (index >= filesToUpload.length) {
        this.props.history.push(`/crud/gallery/${this.props.match.params.albumId}`);
        return;
      }

      const file = filesToUpload[index];

      const formData = new FormData();
      formData.append("album_id", albumId);
      formData.append("photo", file.file); // compressed WebP file

      const url = `${getLocationOrigin()}/tournament/update_album/`;

      const options = { ...defaultOptions };
      options.method = "post";
      options.body = formData;

      fetch(url, options)
        .then(checkStatus)
        .then(parseJSON)
        .then(() => {
          this.setState((prev) => ({
            uploadedFiles: prev.uploadedFiles.map((f) =>
              f.id === file.id ? { ...f, status: "success" } : f
            ),
          }));

          uploadNext(index + 1);
        })
        .catch(() => {
          this.setState((prev) => ({
            uploadedFiles: prev.uploadedFiles.map((f) =>
              f.id === file.id ? { ...f, status: "error" } : f
            ),
          }));

          uploadNext(index + 1);
        });
    };

    uploadNext(0);
  };

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({ isDragging: true });
  };

  handleDragLeave = () => {
    this.setState({ isDragging: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    this.setState({ isDragging: false });

    this.handleFileSelect(e.dataTransfer.files);
  };

  render() {
    const { uploadedFiles, isDragging, uploading } = this.state;

    const stillCompressing = uploadedFiles.some((f) => f.status === "compressing");
    const uploadableFiles = uploadedFiles.filter((f) => f.status !== "too_large");

    return (
      <div className="aap-container">
        {/* Header */}
        <div className="aap-header">
          <Link to={`/crud/gallery/${this.props.match.params.albumId}`} className="aap-back-btn">
            ←
          </Link>

          <div>
            <h1 className="aap-title">Add New Photos</h1>
          </div>
        </div>

        <div className="aap-card">
          {/* Upload Area */}
          <div
            className={"aap-upload-area " + (isDragging ? "aap-dragging" : "")}
            onDragOver={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
          >
            <div className="aap-upload-icon">⬆</div>

            <h3>Drag & Drop Photos</h3>
            <p>or</p>

            <button className="aap-btn-primary" onClick={this.openFileBrowser}>
              Browse Files
            </button>

            <input
              ref={(input) => (this.fileInput = input)}
              type="file"
              multiple
              accept="image/*"
              className="aap-hidden-input"
              onChange={(e) => this.handleFileSelect(e.target.files)}
            />
          </div>

          {/* File List */}
          {uploadedFiles.length > 0 && (
            <div className="aap-file-list">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="aap-file-item">
                  <img
                    src={file.preview}
                    alt={file.file.name}
                    className="aap-thumbnail"
                  />

                  <div className="aap-file-info">
                    <p className="aap-file-name">{file.file.name}</p>

                    {file.status === "too_large" && (
                      <span className="aap-status error">⚠ Exceeds 5MB limit</span>
                    )}

                    {file.status === "compressing" && (
                      <span className="aap-status uploading">Compressing...</span>
                    )}

                    {file.status === "ready" && (
                      <span className="aap-status success">✓ Ready</span>
                    )}

                    {file.status === "uploading" && (
                      <span className="aap-status uploading">Uploading...</span>
                    )}

                    {file.status === "success" && (
                      <span className="aap-status success">✓ Uploaded</span>
                    )}

                    {file.status === "error" && (
                      <span className="aap-status error">⚠ Failed</span>
                    )}
                  </div>

                  <button
                    className="aap-remove-btn"
                    onClick={() => this.removeFile(file.id)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="aap-actions">
            <Link to={`/crud/gallery/${this.props.match.params.albumId}`}>
              <button className="aap-btn-outline">Cancel</button>
            </Link>

            <button
              className="aap-btn-primary"
              onClick={this.handleSubmit}
              disabled={uploadableFiles.length === 0 || uploading || stillCompressing}
            >
              {stillCompressing
                ? "Compressing..."
                : uploading
                ? "Uploading..."
                : "Upload Photo(s)"}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(AddAlbumPhotosLater);


// WEBPACK FOOTER //
// ./src/app/views/partner/crud/gallery/viewGalleryItem/update/photos/index.js